import { useContext, useEffect, useState } from 'preact/hooks';
import { SessionContext } from '../app.jsx';
import { get, post } from '../api.js';
import { Link, navigate } from '../router.js';
import { MonthGrid } from '../ui/MonthGrid.jsx';
import { Tag } from '../ui/Tag.jsx';
import { todayBelgrade, monthOf, isDate, fmtRange, fmtDayTitle } from '../../lib/dates.js';

const covers = (d, date) => d.start_date <= date && d.end_date >= date;
// A tour that is over: it needs no guide any more, and a guide cannot sign up for it.
export const isOver = (d, today = todayBelgrade()) => d.end_date < today;
export const guideNames = (guides) => (guides.length ? `Vodiči: ${guides.map((g) => g.name).join(', ')}` : null);

// SC-001 (guide) and SC-005 (office): the month in ?month=, the chosen day in ?day=.
export function Calendar({ query }) {
  const { user, tick } = useContext(SessionContext);
  const today = todayBelgrade();
  const month = /^\d{4}-(0[1-9]|1[0-2])$/.test(query.get('month') ?? '') ? query.get('month') : monthOf(today);
  const day = isDate(query.get('day')) && monthOf(query.get('day')) === month ? query.get('day') : null;
  const [data, setData] = useState(null); // { month, departures }
  const [error, setError] = useState(null);

  const load = () => get(`/calendar?month=${month}`).then((r) => { setData(r); setError(null); }, (e) => setError(e.message));
  useEffect(() => { load(); }, [month, tick]);

  const loaded = data?.month === month;
  const deps = loaded ? data.departures : [];
  const onDay = (date) => deps.filter((d) => covers(d, date));
  const select = (date) => navigate(`/?month=${month}&day=${date}`, { replace: true });
  const office = user.role === 'office';

  const cells = office
    ? (date) => {
      const list = onDay(date);
      const noGuide = (d) => d.guides.length === 0 && !isOver(d, today);
      return {
        count: list.length,
        noGuide: list.some(noGuide),
        label: list.length > 0 && list.flatMap((d) => [
          // the people stand in their own column; the dot is for screen readers and copied text
          <span key={d.id} class="tile-line">
            <span class="tile-name">{d.tour.name}</span>
            {d.people > 0 && <span class="tile-people"><span class="sr-only">{'\u00a0· '}</span>{d.people}</span>}
          </span>,
          noGuide(d) && <span key={'n' + d.id} class="tile-line no-guide">bez vodiča</span>,
        ]),
      };
    }
    : (date) => ({ working: onDay(date).some((d) => d.mine) });

  const grid = <MonthGrid month={month} cells={cells} selected={day} onSelect={select} today={today} />;
  const alert = error && <p class="alert" role="alert">{error}</p>;

  if (office) {
    return (
      <div class="cal cal-office">
        <div class="cal-grid"><h1 class="sr-only">Kalendar</h1>{alert}{grid}</div>
        <aside class="day-panel" aria-label="Izabrani dan">
          {day ? loaded && <DayOffice key={day} day={day} list={onDay(day)} /> : <p class="empty">Klikni na dan da vidiš njegove ture.</p>}
          <Link href="/rezervacije/nova" class="btn">Nova rezervacija</Link>
        </aside>
      </div>
    );
  }
  return (
    <div class="cal cal-guide">
      <h1 class="sr-only">Kalendar</h1>
      {alert}
      {grid}
      {day ? loaded && <DayGuide key={day} day={day} list={onDay(day)} me={user} reload={load} /> : <p class="empty hint">Dodirni dan da vidiš ture.</p>}
    </div>
  );
}

function DayOffice({ day, list }) {
  return (
    <section class="day-list" aria-labelledby="day-title">
      <h2 id="day-title">{fmtDayTitle(day)}</h2>
      {list.length === 0 && <p class="empty">Nema tura tog dana.</p>}
      {list.map((d) => (
        <article key={d.id} class="card" data-testid={`office-day-departure-${d.id}`}>
          <h3><Link href={`/ture/${d.id}`}>{d.tour.name}</Link></h3>
          <p class="card-line">{fmtRange(d.start_date, d.end_date)}</p>
          <p class="card-line num">{d.people} ljudi, {d.bookings} rezervacija</p>
          {d.guides.length > 0 && <p class="card-line">{guideNames(d.guides)}</p>}
          {d.guides.length === 0 && !isOver(d) && (
            <p class="card-line card-act">
              <span class="no-guide">Još nema vodiča</span>
              <Link href={`/ture/${d.id}#vodici`} class="btn-quiet">Dodeli vodiča</Link>
            </p>
          )}
          {d.equipment.length > 0 && (
            <p class="card-line">Oprema: {d.equipment.map((e) => `${e.qty} × ${e.name}`).join(', ')}</p>
          )}
        </article>
      ))}
    </section>
  );
}

function DayGuide({ day, list, me, reload }) {
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(null);
  const signUp = async (d) => {
    setBusy(d.id);
    setErrors((x) => ({ ...x, [d.id]: null }));
    try {
      await post(`/departures/${d.id}/guides`, { guideId: me.id });
      await reload();
    } catch (e) {
      setErrors((x) => ({ ...x, [d.id]: e.message }));
    }
    setBusy(null);
  };
  return (
    <section class="day-list" aria-labelledby="day-title">
      <h2 id="day-title">{fmtDayTitle(day)}</h2>
      {list.length === 0 && <p class="empty">Nema tura tog dana.</p>}
      {list.map((d) => (
        <article key={d.id} class="card" data-testid={`day-departure-${d.id}`}>
          <div class="card-head">
            <h3><Link href={`/ture/${d.id}`}>{d.tour.name}</Link></h3>
            {d.mine && <Tag kind="zauzeto">Zauzeto</Tag>}
          </div>
          <p class="card-line">{fmtRange(d.start_date, d.end_date)}</p>
          <p class="card-line">{guideNames(d.guides) ?? 'Još nema vodiča'}</p>
          <p class="card-line num">{d.people} ljudi</p>
          {!d.mine && !isOver(d) && <button class="btn" disabled={busy === d.id} onClick={() => signUp(d)}>Prijavi se</button>}
          {errors[d.id] && <p class="alert" role="alert">{errors[d.id]}</p>}
        </article>
      ))}
    </section>
  );
}
